import { useMemo } from 'react';
import { Platform } from 'react-native';

import { getCachedAppInstanceId } from '@/feature/analytics/appInstanceId';
import { Jurisdiction, Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { SocialCurrency, useSocialCurrencyStore } from '@/hooks/use-social-currency';
import { useUserStore } from '@/hooks/use-user-store';
import { QueryCache, QueryClient } from '@tanstack/react-query';

// Lazily create a react-query client
export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: false,
            staleTime: 1000 * 15,
        },
    },
    queryCache: new QueryCache({
        onError: async error => {
            if (typeof error === 'object' && error) {
                if ('error_codes' in error && Array.isArray(error.error_codes)) {
                    const { actions, session, guest } = useUserStore.getState();
                    if (error?.error_codes?.includes('keycloak_token_invalid')) {
                        if (guest) {
                            //Do not refresh "guest sessions"
                            return;
                        }
                        // Session expired
                        await actions.refresh(session);
                    }
                    if (error?.error_codes?.includes('keycloak_session_invalid')) {
                        if (guest) {
                            //Do not trigger "logout" for "guest sessions"
                            return;
                        }
                        // Session doesn't exist, force logout
                        await actions.logout();
                    }
                }
            }
        },
    }),
});

/**
 * Hook returning the appropriate Jurisdiction and Authorization headers to be used with Chameleon API.
 * New headers will be returned when the access token or jurisdiction change.
 */
export const useJurisdictionHeaders = (): { [key: string]: string } => {
    const session = useUserStore(state => state.session);
    const jurisdiction = useJurisdictionHeaderValue();
    const userState = useJurisdictionStore(state => state.jurisdiction);

    const headers = useMemo(() => {
        const appInstanceId = getCachedAppInstanceId();
        const baseHeaders: { [key: string]: string } = {
            JURISDICTION: jurisdiction,
            Authorization: `Bearer ${session?.access_token}`,
            'X-Device-Type': Platform.OS,
        };

        if (appInstanceId) {
            baseHeaders['X-App-Instance-Id'] = appInstanceId;
        }

        // Cloudflare state override based on user detected location
        if (userState) {
            baseHeaders['X-CUSTOM-REGION-CODE'] = userState;
        }

        return baseHeaders;
    }, [session?.access_token, jurisdiction, userState]);

    return headers;
};

export type JurisdictionContext = { product?: Product | null; socialCurrency?: SocialCurrency | null };

/**
 * Creates the appropriate Jurisdiction and Authorization headers to be used with Chameleon API.
 */
export const createJurisdictionHeaders = (context: JurisdictionContext = {}): { [key: string]: string } => {
    const product = context.product ?? useJurisdictionStore.getState().product;
    const jurisdiction = useJurisdictionStore.getState().jurisdiction;
    const socialCurrency = context.socialCurrency ?? useSocialCurrencyStore.getState().activeCurrency;
    const accessToken = useUserStore.getState().session?.access_token;
    const jurisdictionHeader = getJurisdictionHeader(product, jurisdiction, socialCurrency);
    const appInstanceId = getCachedAppInstanceId();

    const headers: { [key: string]: string } = {
        JURISDICTION: jurisdictionHeader,
        Authorization: `Bearer ${accessToken}`,
        'X-Device-Type': Platform.OS,
    };

    if (appInstanceId) {
        headers['X-App-Instance-Id'] = appInstanceId;
    }

    // Cloudflare state override based on user detected location
    if (jurisdiction) {
        headers['X-CUSTOM-REGION-CODE'] = jurisdiction;
    }
    return headers;
};

export const getJurisdictionHeader = (
    mode: Product | undefined,
    state: string | undefined,
    socialCurrency: SocialCurrency
) => {
    if (mode === Product.Pickem) {
        return Jurisdiction.FANTASY;
    } else if (mode === Product.Arcade) {
        return Jurisdiction.ARCADE;
    } else if (mode === Product.Sweeps) {
        if (socialCurrency === SocialCurrency.COINS) {
            return Jurisdiction.SWEEPSCOIN;
        }
        return Jurisdiction.SWEEPSCASH;
    } else if (mode === Product.Casino) {
        if (socialCurrency === SocialCurrency.COINS) {
            return Jurisdiction.CASINOCOIN;
        }
        return Jurisdiction.CASINOCASH;
    } else if (mode === Product.Prediction) {
        return Jurisdiction.PREDICTION;
    } else if (mode === Product.Rips) {
        return Jurisdiction.RIPS;
    }
    return state ?? '';
};

/**
 * Returns the jurisdiction header value for the current user (COIN-SBK, CASH-SBK, FANTASY, etc.)
 */
export const useJurisdictionHeaderValue = () => {
    const userState = useJurisdictionStore(state => state.jurisdiction);
    const product = useJurisdictionStore(state => state.product);
    const socialCurrency = useSocialCurrencyStore(state => state.activeCurrency);
    return getJurisdictionHeader(product, userState, socialCurrency);
};
